import { Directive, ElementRef, OnInit, OnDestroy } from '@angular/core';

/**
 * A directive to override the `transform` style applied by external libraries (e.g., Swiper) and replace it with the `scale` style.
 * This is particularly useful when dealing with libraries that apply `transform: translate3d(...) scale(...)` for zooming or scaling effects,
 * and you want to isolate and use only the `scale` value while removing the `transform` property.
 *
 * @see https://github.com/nolimits4web/swiper/issues/7928
 *
 * ### Purpose
 * - Extracts the `scale` value from the `transform` style (e.g., `translate3d(0px, 0px, 0px) scale(1.5)`).
 * - Applies the extracted `scale` value as a standalone `scale` style.
 * - Removes the original `transform` style to avoid conflicts or unwanted rendering effects.
 *
 * ### Fixes
 * - Addresses a specific issue on iOS devices with the third-party Swiper library, where the zoom functionality
 *   relies on `transform: translate3d(...) scale(...)`, causing antialiasing problems. This directive
 *   isolates the `scale` component and eliminates the unnecessary `transform`, improving rendering quality on SAFARI IOS.
 *
 * ### Technical Notes
 * - Uses `MutationObserver` to detect changes to the `style` attribute of the host element.
 * - WebKit browsers (e.g., Safari on iPhone) enable hardware acceleration for 3D transforms, which boosts performance but may degrade antialiasing
 *   when unnecessary 3D transforms (like `translate3d(0, 0, 0)`) are applied. Replacing `transform` with `scale` can mitigate this issue.
 *
 * ### Usage
 * ```html
 * <img src="example.jpg" ridangoTransformOverride>
 * ```
 * - Apply the directive to any element where you want to override `transform` with `scale`.
 * - The directive automatically monitors and adjusts the styles when changes occur.
 */
@Directive({
    selector: '[ridangoTransformOverride]'
})
export class TransformOverrideDirective implements OnInit, OnDestroy {
    private observer: MutationObserver;

    constructor(private el: ElementRef) {}

    ngOnInit() {
        this.observer = new MutationObserver(() => {
            const transform = this.el.nativeElement.style.transform;
            const scale = this.extractScale(transform);

            if (scale !== null) {
                this.el.nativeElement.style.scale = scale;
                this.el.nativeElement.style.transform = 'none';
            }
        });

        this.observer.observe(this.el.nativeElement, {
            attributes: true,
            attributeFilter: ['style']
        });
    }

    ngOnDestroy() {
        this.observer.disconnect();
    }

    private extractScale(transform: string): string | null {
        const match = transform.match(/scale\(([^)]+)\)/);
        return match ? match[1] : null;
    }
}
